import PropTypes from 'prop-types';
import React, {useState, useRef, useEffect, useMemo} from 'react';
import {connect, useDispatch, useSelector} from 'react-redux';
import {bindActionCreators} from 'redux';
import _ from 'lodash';
import {getSessionUser} from 'stores/selectors/sessionSelectors';

import {getLastOpenedProtocol} from 'stores/selectors/protocolSelectors';
import PageLayout from '../../components/Layout/PageLayout';
import {RowLayout} from '../../components/Layout/Layout';
import ProtocolEditorHeaderContainer from './Header/ProtocolEditorHeaderContainer';
import SerialDilutionHeaderContainer from './Header/SerialDilutionHeader/SerialDilutionHeaderContainer';
import BroadcastHeader from './Header/Broadcast/BroadcastHeader';
import LabwareListContainer from './LabwareList/LabwareListContainer';
import TaskListContainer from './TaskList/TaskListContainer';
import CustomCursorWrapper from '../../components/CustomCursorWrapper';
import VirtualTray from './VirtualTray/VirtualTray';
import MenuListContainer from './MenuList/MenuListContainer';
import ArrowLayerContainer from './ArrowLayer/ArrowLayerContainer';
import SerialDilutionWizardView from './SerialDilution/SerialDilutionWizardView';
import PoolingHeader from './Header/Pooling/PoolingHeader';

import {
  ProtocolEditorViewMode,
  TipChannelType,
  TaskType,
  TrayType,
  SerialDilutionStep,
  UserRoleLevel,
  ViewMode,
} from '../../constants/dataEnum';

import * as protocolEditorAction from '../../stores/actions/protocolEditorAction';
import {isTessaraProtocolStillExists, saveProtocol} from '../../stores/business/protocolManagementBusiness';
import {fetchTrayBarcode} from '../../stores/business/trayBusiness';
import {showModalUnsupportedTray} from '../../stores/business/commonModalBusiness';

import withIntl from '../../lib/withIntl';
import {find} from '../../lib/objectHelper';

import {ProtocolEditorContext} from './ContextProviders/ProtocolEditorContext';
import StickyMouseComponents from './StickyMouseComponents';
import NormalizationHeader from './Header/Normalization/NormalizationHeader';
import NormalizationWizardView from './NormalizationPreview/NormalizationWizardView';
import PicklistHeader from './Header/Picklist/PicklistHeader';
import {useStaticCallback} from '../../lib/customHooks';
import {HighlightTaskContext} from './ContextProviders/HighlightTaskContext';
import {LabwareZoomContext} from './ContextProviders/LabwareZoomContext';
import {setLastOpenedProtocol} from '../../stores/actions/protocolAction';
import {isRoleLevel} from '../../stores/business/userManagementBusiness';
import useDebounce from '../../lib/hooks/debounceHooks';
import {changeView} from '../../stores/actions/appStateAction';
import {saveState} from '../../stores/business/stateBusiness';
import {restoreSdWizard} from '../../stores/business/taskListSerialDilutionBusiness';
import {restoreBroadcastWizard} from '../../stores/business/taskListBroadcastBusiness';
import {restorePoolingWizard} from '../../stores/business/taskListPoolingBusiness';

function mapStateToProps(state) {
  const {
    selectedTipChannel,
    customTipCount,
    selectedMenu,
    serialDilutionStep,
    ADModalShown,
    serialDilutionPrevStep,
    autosave,
  } = state.protocolEditorState;

  return {
    protocolEditorStateSelectedTipChannel: selectedTipChannel,
    protocolEditorStateCustomTipCount: customTipCount,
    protocolEditorStateSelectedMenu: selectedMenu,
    protocolEditorStateSerialDilutionStep: serialDilutionStep,
    protocolEditorStateADModalShown: ADModalShown,
    protocolEditorStateAutosave: autosave,
    protocolSerialDilutionPrevStep: serialDilutionPrevStep,
    instrumentState: state.instrumentState,
    cardStateCards: state.cardState.cards,
    cardStateTasks: state.cardState.tasks,
    cardState: state.cardState,
    protocolState: state.protocolState,
    trayCalibrationState: state.trayCalibrationState,
    labwareState: state.labwareState,
    normalizationState: state.normalizationState,
    normalizationStateNormalizationStep: state.normalizationState.currentWizardView,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    protocolEditorStateActionSetSelectedTipChannel: bindActionCreators(
      protocolEditorAction.setSelectedTipChannel,
      dispatch,
    ),
    protocolEditorStateActionSetCustomTipCount: bindActionCreators(protocolEditorAction.setCustomTipCount, dispatch),
    protocolEditorStateActionSetOpenSdPreview: bindActionCreators(protocolEditorAction.setOpenSdPreview, dispatch),
    protocolManagementBusinessSaveProtocol: bindActionCreators(saveProtocol, dispatch),
    trayBusinessFetchTrayBarcode: bindActionCreators(fetchTrayBarcode, dispatch),
    commonModalBusinessShowModalUnsupportedTray: bindActionCreators(showModalUnsupportedTray, dispatch),
    protocolActionSetLastOpenedProtocol: bindActionCreators(setLastOpenedProtocol, dispatch),
    userManagementBusinessIsRoleLevel: bindActionCreators(isRoleLevel, dispatch),
    taskListSerialDilutionBusinessRestoreSdWizard: bindActionCreators(restoreSdWizard, dispatch),
    taskListBroadcastBusinessRestoreBroadcastWizard: bindActionCreators(restoreBroadcastWizard, dispatch),
    taskListPoolingBusinessRestorePoolingWizard: bindActionCreators(restorePoolingWizard, dispatch),
  };
}

function ProtocolEditor(props) {
  const {
    protocolEditorStateSelectedTipChannel,
    protocolEditorStateCustomTipCount,
    protocolEditorStateSelectedMenu,
    protocolEditorStateSerialDilutionStep,
    protocolEditorStateADModalShown,
    protocolEditorStateAutosave,
    protocolSerialDilutionPrevStep,
    normalizationStateNormalizationStep,
    instrumentState,
    cardStateCards,
    cardStateTasks,
    cardState,
    protocolState,
    trayCalibrationState,
    normalizationState,
    labwareState,
    protocolEditorStateActionSetSelectedTipChannel,
    protocolEditorStateActionSetCustomTipCount,
    protocolEditorStateActionSetOpenSdPreview,
    protocolManagementBusinessSaveProtocol,
    trayBusinessFetchTrayBarcode,
    commonModalBusinessShowModalUnsupportedTray,
    protocolActionSetLastOpenedProtocol,
    userManagementBusinessIsRoleLevel,
    taskListSerialDilutionBusinessRestoreSdWizard,
    taskListBroadcastBusinessRestoreBroadcastWizard,
    taskListPoolingBusinessRestorePoolingWizard,
  } = props;
  const selectedCard = find(cardStateCards, {isSelected: true});
  const selectedTaskList = selectedCard ? {[selectedCard.ID]: _.pick(cardStateTasks, selectedCard.tasks)} : {};
  const selectedCardRef = useRef(selectedCard);
  useEffect(() => {
    selectedCardRef.current = selectedCard;
  }, [selectedCard]);

  const [volumeDetailViewChecked, setVolumeDetailViewChecked] = useState(false);
  const [arrowDetailViewChecked, setArrowDetailViewChecked] = useState(false);
  const [taskNumberRefs, setTaskNumberRefs] = useState({}); // will contains { 1: refTaskNumber1, 2: refTaskNumber2, etc }

  // TODO: handle highlighted tasks either with each task's isSelected or by putting the highlighted task on redux to have one source of information
  const [highlightedTask, setHighlightedTask] = useState(null);
  const [isLabwareZoom, setIsLabwareZoom] = useState(false);
  const [deckLabwareRefs, setDeckLabwareRefs] = useState({}); // will contains { 1: refDeckLabware1, 2: refDeckLabware2, etc }
  const [plateLabwareRefs, setPlateLabwareRefs] = useState({
    1: useRef(null),
    3: useRef(null),
    4: useRef(null),
    5: useRef(null),
    6: useRef(null),
  }); // will contains { 1: plateLabwareRefs1, 2: plateLabwareRefs2, etc }
  const [isOverCapacityTooltipShow, setIsOverCapacityTooltipShow] = useState(false);
  const [overCapacityMessage, setOverCapacityMessage] = useState('protocolEditor.pooling.wellOverCapacity');
  const [tooltipPosition, setTooltipPosition] = useState({});
  const [menuListRef, setMenuListRef] = useState(useRef(null));

  const [sdProperty, setSdProperty] = useState({
    sampleVolume: selectedCard?.sdProperty?.sampleVolume || 0,
    ratio: selectedCard?.sdProperty?.ratio || 1,
    diluentVolume: selectedCard?.sdProperty?.diluentVolume || 0,
    step: selectedCard?.sdProperty?.step || 1,
    reuseTips: selectedCard?.sdProperty?.reuseTips ?? true,
    gradationView: false,
    replicateWell: selectedCard?.sdProperty?.replicateWell || 1,
  });

  const lastOpenedProtocol = useSelector(getLastOpenedProtocol);
  const user = useSelector(getSessionUser);
  const isProtocolOwner = useMemo(() => lastOpenedProtocol?.owner === user.userId, [lastOpenedProtocol, user]);

  useEffect(() => {
    if (
      protocolEditorStateSerialDilutionStep !== SerialDilutionStep.SERIAL_DILUTION_SETTINGS ||
      protocolEditorStateSelectedMenu !== ProtocolEditorViewMode.SERIAL_DILUTION
    ) {
      protocolEditorStateActionSetOpenSdPreview(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [protocolEditorStateSerialDilutionStep]);

  const contentRef = useRef(null);
  const virtualTrayRef = useRef(null);
  const svmExcludedComponentRef = useRef(null);

  const initialCardStateCardsRef = useRef(null);
  const initialCardStateTasksRef = useRef(null);
  const initialProtocolStateRef = useRef(null);
  const initialLabwareStateRef = useRef(null);

  const cardStateCardsRef = useRef(null);
  const cardStateTasksRef = useRef(null);
  const protocolStateRef = useRef(null);
  const labwareStateRef = useRef(labwareState);

  const headerRef = useRef(null);
  const tipChannelRef = useRef(null);
  const customTipRef = useRef(null);

  const [draggedLabwareId, setDraggedLabwareId] = useState(null);
  const [draggedLabwareType, setDraggedLabwareType] = useState(null);
  const [labwareFilterParameter, setLabwareFilterParameter] = useState(null);
  const [menuListScrollPosition, setMenuListScrollPosition] = useState(null);
  const [zoomWidth, setZoomWidth] = useState(null);
  const [zoomHeight, setZoomHeight] = useState(null);

  const isOperator = userManagementBusinessIsRoleLevel(UserRoleLevel.OPERATOR);
  const isTessaraSuperUser = userManagementBusinessIsRoleLevel(UserRoleLevel.TESSARA_SUPER_USER);

  const {isTessaraProtocol} = protocolState.lastOpenedProtocol || {};
  const {isSimulationMode, isAutomationMode} = instrumentState;

  const isTessaraRegularUserOpenTessaraProtocol = !isTessaraSuperUser && isTessaraProtocol;

  const dispatch = useDispatch();

  // #region auto save protocol

  useEffect(() => {
    cardStateCardsRef.current = cardStateCards;
    cardStateTasksRef.current = cardStateTasks;
    protocolStateRef.current = protocolState;
    labwareStateRef.current = labwareState;
    virtualTrayRef.current.resetBroadcastPoolingCurrentLabware();
  }, [cardStateCards, cardStateTasks, protocolState, labwareState]);

  useDebounce(protocolEditorStateAutosave ? [cardState, labwareState, normalizationState] : [null, null], () =>
    protocolManagementBusinessSaveProtocol(false, false),
  );

  useEffect(() => {
    initialCardStateCardsRef.current = cardStateCards;
    initialCardStateTasksRef.current = cardStateTasks;
    initialProtocolStateRef.current = protocolState;
    initialLabwareStateRef.current = labwareState;

    protocolEditorStateActionSetSelectedTipChannel(null);
    protocolEditorStateActionSetCustomTipCount(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // #endregion auto save protocol

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(async () => {
    const checkCompatibilityTray = async (trayType) => {
      const installedTrayType = trayType ?? trayCalibrationState.trayType;
      const protocolTrayType = protocolState.lastOpenedProtocol?.trayType || '';

      if (
        (!protocolTrayType && installedTrayType !== TrayType.NORMAL_CADDY) ||
        (protocolTrayType && protocolTrayType !== installedTrayType)
      ) {
        if (!isSimulationMode || (isSimulationMode && isAutomationMode)) commonModalBusinessShowModalUnsupportedTray();
      }
    };

    const doFetchTrayBarcode = async () => {
      let trayType;
      let trayId = trayCalibrationState.id;
      if (trayId < 0) {
        const res = await trayBusinessFetchTrayBarcode();
        trayType = res.tray.trayType;
        trayId = res.tray.id;
      }

      const usedLabwareID = Object.values(labwareStateRef.current)
        .map((labware) => labware.LabwareID)
        .filter(Boolean);

      if (!Object.keys(cardStateTasksRef.current).length && !usedLabwareID.length) {
        protocolActionSetLastOpenedProtocol({
          ...protocolState.lastOpenedProtocol,
          trayType: trayCalibrationState.trayType,
        });
      } else if (trayId > 0) {
        checkCompatibilityTray(trayType);
      }
    };

    await doFetchTrayBarcode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trayCalibrationState.trayType]);

  useEffect(() => {
    const {lastOpenedProtocol} = protocolState;

    if (lastOpenedProtocol?.isTessaraProtocol && Object.keys(selectedTaskList).length > 0) {
      dispatch(
        isTessaraProtocolStillExists(lastOpenedProtocol.id, () => {
          if (!isOperator) dispatch(changeView(ViewMode.EXPLORER));
          dispatch(saveState());
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTaskEdit = useStaticCallback((taskID, cardID, cardType, isNewAirGapTask = false) => {
    virtualTrayRef.current.editTask(taskID, cardID, cardType, isNewAirGapTask);
  });

  const handleNotifyDeleteCard = useStaticCallback((cardType) => {
    virtualTrayRef.current.deleteCard(cardType);
  });

  const handleNotifyDeleteTask = useStaticCallback((taskID, cardID, cardType) => {
    virtualTrayRef.current.deleteTask(taskID, cardID, cardType);
  });

  function handleNotifyNormalizationHeaderBack() {
    virtualTrayRef.current.normalizationHeaderBack();
  }

  function handleNotifyNormalizationHeaderNext() {
    virtualTrayRef.current.normalizationHeaderNext();
  }

  function handleNotifyNormalizationHeaderCancel() {
    virtualTrayRef.current.normalizationHeaderCancel();
  }

  function handleNotifyNormalizationHeaderCancelEdit() {
    virtualTrayRef.current.normalizationHeaderCancelEdit();
  }

  function handleNotifyBroadcastHeaderBack() {
    virtualTrayRef.current.broadcastHeaderBack();
  }

  function handleNotifyBroadcastHeaderCancel() {
    virtualTrayRef.current.broadcastHeaderCancel();
  }

  function handleNotifyBroadcastHeaderCancelEdit() {
    virtualTrayRef.current.broadcastHeaderCancelEdit();
  }

  function handleNotifyBroadcastHeaderApply() {
    virtualTrayRef.current.broadcastHeaderApply();
  }

  function handleNotifyBroadcastShowVolumeModal() {
    virtualTrayRef.current.broadcastShowVolumeModal();
  }

  function handleNotifyPoolingHeaderBack() {
    virtualTrayRef.current.poolingHeaderBack();
  }

  function handleNotifyPoolingHeaderCancel() {
    virtualTrayRef.current.poolingHeaderCancel();
  }

  function handleNotifyPoolingHeaderCancelEdit() {
    virtualTrayRef.current.poolingHeaderCancelEdit();
  }

  function handleNotifyPoolingNext() {
    virtualTrayRef.current.onNextPooling();
  }

  function handleNotifyPoolingShowVolumeModal(deckNumber) {
    virtualTrayRef.current.poolingShowVolumeModal(deckNumber);
  }

  function setHoveredLabwareId(labwareID) {
    virtualTrayRef.current.setHoveredLabwareId(labwareID);
  }

  function poolingSetAllowedTips() {
    virtualTrayRef.current.poolingSetAllowedTips();
  }

  function handleGlobalUndo() {
    virtualTrayRef.current.onGlobalUndo();
  }

  function handleGlobalRedo() {
    virtualTrayRef.current.onGlobalRedo();
  }

  // restoring wizard views when open protocol
  useEffect(() => {
    taskListSerialDilutionBusinessRestoreSdWizard();
    taskListBroadcastBusinessRestoreBroadcastWizard(handleNotifyBroadcastShowVolumeModal);
    taskListPoolingBusinessRestorePoolingWizard(handleNotifyPoolingShowVolumeModal);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cursorUrl = useMemo(() => {
    if (protocolEditorStateSelectedMenu !== ProtocolEditorViewMode.SIMPLE_TRANSFER) return '';

    const taskPickTip = find(cardStateTasks, {
      ID: selectedCard?.tasks,
      type: TaskType.PICK_TIP,
    });

    if (!taskPickTip) return '';

    const taskEjectTip = find(cardStateTasks, {
      ID: selectedCard?.tasks,
      type: TaskType.EJECT_TIP,
    });

    if (taskEjectTip) return '';

    switch (protocolEditorStateSelectedTipChannel) {
      case TipChannelType.TIP_CHANNEL_1:
        return './svg/protocol-editor/cursor-tip-1.svg';
      case TipChannelType.TIP_CHANNEL_8:
        return './svg/protocol-editor/cursor-tip-8.svg';
      case TipChannelType.TIP_CHANNEL_12:
        return './svg/protocol-editor/cursor-tip-12.svg';
      case TipChannelType.TIP_CHANNEL_96:
        if (protocolEditorStateCustomTipCount) {
          return './svg/protocol-editor/cursor-tip-custom.svg';
        }
        return './svg/protocol-editor/cursor-tip-96.svg';
      case TipChannelType.TIP_CHANNEL_CUSTOM:
        return './svg/protocol-editor/cursor-tip-custom.svg';
      default:
        return '';
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [protocolEditorStateSelectedTipChannel, protocolEditorStateSelectedMenu, Object.keys(cardStateTasks).length]);

  const highlightTaskContextValue = useMemo(
    () => ({
      highlightedTask,
      setHighlightedTask,
    }),
    [highlightedTask],
  );

  const labwareZoomContextValue = useMemo(
    () => ({
      isLabwareZoom,
      setIsLabwareZoom,
    }),
    [isLabwareZoom],
  );

  const protocolEditorContextValue = useMemo(
    () => ({
      volumeDetailViewChecked,
      setVolumeDetailViewChecked,

      arrowDetailViewChecked,
      setArrowDetailViewChecked,

      taskNumberRefs,
      setTaskNumberRefs,

      deckLabwareRefs,
      setDeckLabwareRefs,

      plateLabwareRefs,
      setPlateLabwareRefs,

      selectedCardRef,

      sdProperty,
      setSdProperty,

      draggedLabwareId,
      setDraggedLabwareId,

      draggedLabwareType,
      setDraggedLabwareType,

      cardStateCardsRef,
      cardStateTasksRef,

      poolingSetAllowedTips,

      setIsOverCapacityTooltipShow,
      isOverCapacityTooltipShow,
      tooltipPosition,
      setTooltipPosition,
      overCapacityMessage,
      setOverCapacityMessage,

      headerRef,
      setHoveredLabwareId,

      labwareFilterParameter,
      setLabwareFilterParameter,

      menuListScrollPosition,
      setMenuListScrollPosition,

      zoomWidth,
      setZoomWidth,

      zoomHeight,
      setZoomHeight,
    }),
    [
      volumeDetailViewChecked,
      arrowDetailViewChecked,
      taskNumberRefs,
      deckLabwareRefs,
      plateLabwareRefs,
      selectedCardRef,
      cardStateCardsRef,
      cardStateTasksRef,
      sdProperty,
      draggedLabwareId,
      isOverCapacityTooltipShow,
      tooltipPosition,
      overCapacityMessage,
      headerRef,
      labwareFilterParameter,
      menuListScrollPosition,
      zoomWidth,
      zoomHeight,
      draggedLabwareType,
    ],
  );

  function renderHeader() {
    switch (protocolEditorStateSelectedMenu) {
      case ProtocolEditorViewMode.SERIAL_DILUTION:
        return <SerialDilutionHeaderContainer selectedCard={selectedCard} />;
      case ProtocolEditorViewMode.NORMALIZATION:
        return (
          <NormalizationHeader
            notifyBack={handleNotifyNormalizationHeaderBack}
            notifyNext={handleNotifyNormalizationHeaderNext}
            notifyCancel={handleNotifyNormalizationHeaderCancel}
            notifyCancelEdit={handleNotifyNormalizationHeaderCancelEdit}
            headerRef={headerRef}
          />
        );
      case ProtocolEditorViewMode.BROADCAST:
        return (
          <BroadcastHeader
            notifyBack={handleNotifyBroadcastHeaderBack}
            notifyCancel={handleNotifyBroadcastHeaderCancel}
            notifyCancelEdit={handleNotifyBroadcastHeaderCancelEdit}
            notifyNext={handleNotifyBroadcastHeaderApply}
            headerRef={headerRef}
          />
        );
      case ProtocolEditorViewMode.POOLING:
        return (
          <PoolingHeader
            notifyBack={handleNotifyPoolingHeaderBack}
            notifyCancel={handleNotifyPoolingHeaderCancel}
            notifyCancelEdit={handleNotifyPoolingHeaderCancelEdit}
            notifyNext={handleNotifyPoolingNext}
            headerRef={headerRef}
          />
        );
      case ProtocolEditorViewMode.PICKLIST:
        return <PicklistHeader headerRef={headerRef} />;
      default:
        return (
          <ProtocolEditorHeaderContainer
            headerRef={headerRef}
            onUndo={handleGlobalUndo}
            onRedo={handleGlobalRedo}
            runOnly={isOperator || isTessaraRegularUserOpenTessaraProtocol || !isProtocolOwner}
            isTessaraSuperUser={isTessaraSuperUser}
            isTessaraProtocol={isTessaraProtocol}
          />
        );
    }
  }

  function isViewMode(type) {
    const isSerialDilution =
      protocolEditorStateSelectedMenu === ProtocolEditorViewMode.SERIAL_DILUTION && protocolSerialDilutionPrevStep;
    const isOnPlateSelectorWizard =
      protocolEditorStateSelectedMenu === ProtocolEditorViewMode.NORMALIZATION ||
      protocolEditorStateSelectedMenu === ProtocolEditorViewMode.PICKLIST;

    switch (type) {
      case 1: // labware list
        return !isOperator && !isTessaraRegularUserOpenTessaraProtocol && isProtocolOwner;
      case 2: // menu list
        return (
          !isOperator &&
          !isSerialDilution &&
          !isOnPlateSelectorWizard &&
          !isTessaraRegularUserOpenTessaraProtocol &&
          isProtocolOwner
        );
      case 3: // task list
        return !isSerialDilution && !isOnPlateSelectorWizard && !isTessaraRegularUserOpenTessaraProtocol;
      default:
        return true;
    }
  }

  function zoomEventHandler(e) {
    const {width, height} = e.currentTarget;
    setZoomWidth(width);
    setZoomHeight(height);
  }

  window.visualViewport.addEventListener('resize', zoomEventHandler);

  function renderMenuContent() {
    return (
      <CustomCursorWrapper cursorUrl={cursorUrl}>
        <RowLayout>
          <VirtualTray
            ref={virtualTrayRef}
            selectedCard={selectedCard}
            runtimeMode={isTessaraRegularUserOpenTessaraProtocol}
            hideWasteBin={isTessaraRegularUserOpenTessaraProtocol}
            svmExcludedComponentRef={svmExcludedComponentRef}
            isTessaraProtocol={isTessaraProtocol}
            hideWellHighlight={isTessaraRegularUserOpenTessaraProtocol}
            isRunOnly={isOperator || !isProtocolOwner}
          />
          {isViewMode(2) ? (
            <MenuListContainer
              selectedCard={selectedCard}
              volumeDetailViewChecked={volumeDetailViewChecked}
              onAirGapPressed={handleTaskEdit}
              setMenuListDivRef={(ref) => setMenuListRef(ref)}
              tipChannelRef={tipChannelRef}
              customTipRef={customTipRef}
            />
          ) : (
            // eslint-disable-next-line react/jsx-no-useless-fragment
            <></>
          )}
          <ArrowLayerContainer selectedCard={selectedCard} tasks={cardStateTasksRef.current} />
          <StickyMouseComponents
            selectedCard={selectedCard}
            tooltipVisible={isOverCapacityTooltipShow}
            initMousePosition={tooltipPosition}
            tooltipMessage={overCapacityMessage}
            protocolEditorMode={protocolEditorStateSelectedMenu}
          />
        </RowLayout>
      </CustomCursorWrapper>
    );
  }

  return (
    <ProtocolEditorContext.Provider value={protocolEditorContextValue}>
      <HighlightTaskContext.Provider value={highlightTaskContextValue}>
        <LabwareZoomContext.Provider value={labwareZoomContextValue}>
          <PageLayout>
            <PageLayout.Header>{renderHeader()}</PageLayout.Header>
            <PageLayout.Content ref={contentRef}>
              <RowLayout>
                {isViewMode(1) ? (
                  <LabwareListContainer
                    filterParameter={labwareFilterParameter}
                    setDraggedLabwareId={setDraggedLabwareId}
                    setDraggedLabwareType={setDraggedLabwareType}
                  />
                ) : (
                  // eslint-disable-next-line react/jsx-no-useless-fragment
                  <></>
                )}
                {renderMenuContent()}
                {isViewMode(3) ? (
                  <TaskListContainer
                    onTaskEdit={handleTaskEdit}
                    notifyDeleteCard={handleNotifyDeleteCard}
                    notifyDeleteTask={handleNotifyDeleteTask}
                    selectedCard={selectedCard}
                    isADModalShown={protocolEditorStateADModalShown}
                    highlightedTask={highlightedTask}
                    setHighlightedTask={setHighlightedTask}
                    refList={headerRef}
                    selectedTaskList={selectedTaskList}
                    virtualTrayRef={virtualTrayRef}
                    menuListRef={menuListRef}
                    editablePickTipExcludedRef={[tipChannelRef, customTipRef]}
                    svmExcludedComponentRef={svmExcludedComponentRef}
                    readOnly={isOperator || !isProtocolOwner}
                  />
                ) : (
                  // eslint-disable-next-line react/jsx-no-useless-fragment
                  <></>
                )}
              </RowLayout>
              {contentRef && contentRef.current && (
                <SerialDilutionWizardView
                  show={
                    protocolEditorStateSerialDilutionStep === SerialDilutionStep.SERIAL_DILUTION_SETTINGS &&
                    protocolEditorStateSelectedMenu === ProtocolEditorViewMode.SERIAL_DILUTION
                  }
                  selectedCard={selectedCard}
                />
              )}
              {contentRef && contentRef.current && (
                <NormalizationWizardView
                  show={normalizationStateNormalizationStep === 3 || normalizationStateNormalizationStep === 4}
                  container={contentRef.current}
                  selectedCard={selectedCard}
                  normalizationState={normalizationState}
                />
              )}
            </PageLayout.Content>
          </PageLayout>
        </LabwareZoomContext.Provider>
      </HighlightTaskContext.Provider>
    </ProtocolEditorContext.Provider>
  );
}

ProtocolEditor.propTypes = {
  protocolEditorStateSelectedTipChannel: PropTypes.any,
  protocolEditorStateCustomTipCount: PropTypes.object,
  protocolEditorStateSelectedMenu: PropTypes.any.isRequired,
  protocolEditorStateSerialDilutionStep: PropTypes.any.isRequired,
  protocolEditorStateADModalShown: PropTypes.any.isRequired,
  protocolEditorStateAutosave: PropTypes.bool.isRequired,
  protocolSerialDilutionPrevStep: PropTypes.any,
  normalizationStateNormalizationStep: PropTypes.any,
  instrumentState: PropTypes.object.isRequired,
  cardStateCards: PropTypes.object.isRequired,
  cardStateTasks: PropTypes.object.isRequired,
  cardState: PropTypes.object.isRequired,
  protocolState: PropTypes.object.isRequired,
  labwareState: PropTypes.object.isRequired,
  trayCalibrationState: PropTypes.object.isRequired,
  normalizationState: PropTypes.object.isRequired,
  protocolEditorStateActionSetSelectedTipChannel: PropTypes.func.isRequired,
  protocolEditorStateActionSetCustomTipCount: PropTypes.func.isRequired,
  protocolEditorStateActionSetOpenSdPreview: PropTypes.func.isRequired,
  protocolManagementBusinessSaveProtocol: PropTypes.func.isRequired,
  trayBusinessFetchTrayBarcode: PropTypes.func.isRequired,
  commonModalBusinessShowModalUnsupportedTray: PropTypes.func.isRequired,
  protocolActionSetLastOpenedProtocol: PropTypes.func.isRequired,
  userManagementBusinessIsRoleLevel: PropTypes.func.isRequired,
  taskListSerialDilutionBusinessRestoreSdWizard: PropTypes.func.isRequired,
  taskListBroadcastBusinessRestoreBroadcastWizard: PropTypes.func.isRequired,
  taskListPoolingBusinessRestorePoolingWizard: PropTypes.func.isRequired,
};

ProtocolEditor.defaultProps = {
  protocolEditorStateCustomTipCount: null,
  normalizationStateNormalizationStep: null,
  protocolEditorStateSelectedTipChannel: null,
  protocolSerialDilutionPrevStep: null,
};

export default connect(mapStateToProps, mapDispatchToProps)(withIntl(ProtocolEditor));
